import type * as React from 'react';
import { getPaginated } from '@Domain/Api';
import accountRolesFetched, {
	type AccountRolesFetchedAction,
} from '@Application/Security/Roles/Actions/accountRolesFetched';
import accountRolesFetchFailed, {
	type AccountRolesFetchFailedAction,
} from '@Application/Security/Roles/Actions/accountRolesFetchFailed';
import type { RolePayload } from '@Application/Security/Roles/Actions/rolesFetched';
import type FetchAccountRoles from '@Application/Security/Roles/Queries/FetchAccountRoles';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

type Actions = AccountRolesFetchedAction | AccountRolesFetchFailedAction;

// `getPaginated` requires a mutable `results` array (it pushes subsequent pages into it); the
// dispatched action still carries the `readonly` form declared by `AccountRolesListFetchedPayload`.
type PaginatedAccountRoles = {
	totalRecords: number;
	results: RolePayload[];
};

/**
 * Feeds the role selector of the user-management screen: the account's whole role catalogue,
 * unfiltered. Writes to `rolesStore.accountRoles`, never to `rolesStore.rolesList` (owned by
 * `fetchRoles`).
 */
export default (query: FetchAccountRoles) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// Page indexing starts at 0 server-side, and `getPaginated` only walks every page when called
			// with `page: 0` — its internal guard only catches `page > 1`.
			const response = await getPaginated<PaginatedAccountRoles>(
				`${parameters.api.host}/api/accounts/${query.accountId}/roles`,
				{ page: 0, rowsPerPage: 200 },
			);

			const action = accountRolesFetched(response, query.accountId);
			dispatch(action);
			return action;
		} catch (error) {
			const action = accountRolesFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
